import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EnvironmentHelperService } from '@shared/services/environment-helper.service';
import { first } from 'rxjs/operators';
import { CommunicatorService } from './communicator.service';
import { FeatureService } from './feature.service';
import { NotificationService } from './notification.service';

/*
 *  Takes either a translation key as a string
 *  or an object of form { message: string, params: object }
 *  for passing params to a translation
 *
 *  Assumes the translations have been loaded!
 */

interface IMessengerServiceOptionsObj {
  [x: string]: any;
  message?: string;
  params?: object;
  error?: Error;
  hideAfter?: number;
}

// eslint-disable-next-line @typescript-eslint/naming-convention, no-var
declare var Messenger: any;

/** Service that uses window Messenger to communicate with the parent app */
@Injectable()
export class MessengerService {
  constructor(
    public communicator: CommunicatorService,
    public translate: TranslateService,
    private readonly feature: FeatureService,
    private readonly envHelper: EnvironmentHelperService,
    private readonly notification: NotificationService
  ) {
    Messenger.options = {
      extraClasses: 'messenger-fixed messenger-on-top messenger-on-right',
      theme: 'ice',
      messageDefaults: {
        hideAfter: 5,
        hideOnNavigate: true,
        events: {
          mouseenter: () => clearTimeout(Messenger._hideTimeout)
        }
      }
    };
  }

  public error(options: string | IMessengerServiceOptionsObj): any {
    let optionsObject: IMessengerServiceOptionsObj = this.mergeOptions(options, 'error');
    if (optionsObject.error) {
      console.error(optionsObject.error);
    }

    return this.send(optionsObject);
  }

  public success(options: string | IMessengerServiceOptionsObj): any {
    let optionsObject: IMessengerServiceOptionsObj = this.mergeOptions(options, 'success');

    return this.send(optionsObject);
  }

  public info(options: string | IMessengerServiceOptionsObj): any {
    let optionsObject: IMessengerServiceOptionsObj = this.mergeOptions(options, 'info');

    return this.send(optionsObject);
  }

  private mergeOptions(options: string | IMessengerServiceOptionsObj, type: string) {
    let optionsObj: IMessengerServiceOptionsObj = {
      ...(typeof options !== 'string' && options),
      message:
        typeof options === 'string'
          ? this.translate.instant(options)
          : this.translate.instant(options.message, options.params),
      id: typeof options === 'string' ? options : options.id ? options.id : options.message,
      type,
      hideAfter: typeof options !== 'string' && options.hideAfter ? options.hideAfter : 5
    };

    delete optionsObj.params;

    return optionsObj;
  }

  private send(options: any): any {
    if (!this.envHelper.isIframed) {
      return Messenger().post(options);
    }

    this.feature
      .hasFeature('adminUI.v3.IFC.migration')
      .pipe(first())
      .subscribe((hasIfc) => {
        if (hasIfc) {
          const { message, ...custom } = options;
          this.notification.requestNotification(message, custom);
        } else {
          this.communicator.toast(options);
        }
      });
  }
}
